import React, { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import '../styles/card.css';
import Loader from '../components/Loader';

const MyCard = ({ user }) => {
    // State for loading, error, and submission tracking
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [applicationNumber, setApplicationNumber] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Form data for the card application
    const [formData, setFormData] = useState({
        cardType: '',
        address: '',
        applicationNumber: '',
    });

    // User-specific data and transaction summary
    const [userData, setUserData] = useState(null);
    const [summary, setSummary] = useState({
        totalCredits: 0,
        totalDebits: 0,
        totalTransactions: 0,
    });

    useEffect(() => {
        // Fetches user data from Firestore and calculates transaction summary
        const fetchUserData = async () => {
            try {
                const userId = user.uid;
                const userDoc = await getDoc(doc(db, 'alpha', userId));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setUserData(data);

                    // Process transactions for summary
                    let fetchedTransactions = data.transactions || [];
                    fetchedTransactions = fetchedTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
                    const totalCredits = fetchedTransactions.reduce(
                        (acc, transaction) => transaction.type === 'credit' ? acc + transaction.amount : acc,
                        0
                    );
                    const totalDebits = fetchedTransactions.reduce(
                        (acc, transaction) => transaction.type === 'debit' ? acc + transaction.amount : acc,
                        0
                    );
                    const totalTransactions = fetchedTransactions.length;

                    setSummary({ totalCredits, totalDebits, totalTransactions });
                } else {
                    setError('User not found.');
                }
            } catch (error) {
                setError('Error fetching user data.');
                console.error('Error fetching user data: ', error);
            } finally {
                setLoading(false);
            }
        };

        if (user && user.uid) {
            fetchUserData();
        }
    }, [user]);

    // Updates the form state based on input
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    // Deletes the existing card application
    const handleDeleteApplication = async () => {
        try {
            setSubmitting(true);
            await setDoc(
                doc(db, 'alpha', user.uid),
                { ...userData, cardApplication: null },
                // Only update cardApplication
                { merge: true }
            );
            // Reload the page to reflect changes
            window.location.reload();
        } catch (error) {
            console.error('Error deleting card application:', error);
        } finally {
            setSubmitting(false);
        }
    };

    // Submits the card application and saves to Firestore
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const currentDate = new Date();
            const day = currentDate.getDate();
            const month = currentDate.getMonth() + 1;
            const dateAndMonthSum = day + month;
            const generatedApplicationNumber = dateAndMonthSum * userData.accountNumber;

            const updatedFormData = {
                ...formData,
                applicationNumber: generatedApplicationNumber,
            };

            // Save application to Firestore
            await setDoc(doc(db, 'alpha', user.uid), {
                ...userData,
                cardApplication: updatedFormData,
            });

            setApplicationNumber(generatedApplicationNumber);
            setFormData(updatedFormData);
            // Reload to display the submitted data
            window.location.reload();
        } catch (error) {
            console.error('Error submitting card application: ', error);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div><Loader /></div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <div className="transactions-container" id='card'>
            {/* Display transaction summary */}
            <div className="summary-boxed">
                <div className="summaryBoxes">
                    <>Total Credits: ${summary.totalCredits.toFixed(2)}</>
                </div>
                <div className="summaryBoxes">
                    <>Total Debits: ${summary.totalDebits.toFixed(2)}</>
                </div>
                <div className="summaryBoxes">
                    <>Total Transactions: {summary.totalTransactions}</>
                </div>
            </div>

            {/* Show application number if submitted */}
            {applicationNumber && (
                <div className="application-info">
                    Application sent!
                </div>
            )}

            <div className="form-card-container">
                {/* Display user card details */}
                <div className="user-card">
                    <h2>Available Card</h2>
                    <div className="card-details">
                        <div className="mastercard">
                            <div className="card-top">
                                <div className="chip"></div>
                                <div className="logos"></div>
                            </div>
                            <div className="card-number">{userData.accountNumber}</div>
                            <div className="card-info">
                                <div className="info-row">
                                    <div className="label">Cardholder Name:</div>
                                    <div className="value">{userData.fullName}</div>
                                </div>
                                <div className="info-row">
                                    <div className="label">Expiration Date:</div>
                                    <div className="value">03/26</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form to apply for a card */}
                <div className="card-form">
                    {userData.cardApplication?.address ? (
                        <>
                            <h2>You have already applied for a card</h2>
                            Your {userData.cardApplication.cardType} application number is {userData.cardApplication.applicationNumber}
                            <p>Please send this Application Number to our email <strong>support@skyhighntb.com</strong> to request for your Card.</p>
                            <p><strong>Billing Address:</strong> {userData.cardApplication.address}</p>
                            <div
                                onClick={handleDeleteApplication}
                                className="log-out"
                                disabled={submitting}
                            >
                                {submitting ? 'Cancelling...' : 'Cancel Application'}
                            </div>
                        </>
                    ) : (
                        <>
                            <h2>Apply for a Card</h2>
                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label htmlFor="cardType" className='label'>Card Type:</label>
                                    <select
                                        id="cardType"
                                        name="cardType"
                                        value={formData.cardType}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Card Type</option>
                                        <option value="Credit Card">Credit Card</option>
                                        <option value="Debit Card">Debit Card</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="address" className='label'>Address:</label>
                                    <input
                                        type="text"
                                        id="address"
                                        name="address"
                                        className='input-field'
                                        placeholder='eg. 123 Address Street, NY, USA'
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <button type="submit" className="submit-button" disabled={submitting}>
                                    {submitting ? 'Applying ...' : 'Apply'}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyCard;
